import { useEffect } from "react";

/**
 * Lightweight document.head SEO manager — no react-helmet dependency.
 *
 * Sets/updates <title>, meta description, canonical, Open Graph, Twitter
 * card, and (optionally) a schema.org JSON-LD block. Cleans up its
 * additions on unmount so other pages don't inherit stale tags.
 */
export default function useSEO({
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  schema,
} = {}) {
  useEffect(() => {
    const prev = { title: document.title };
    const tagRefs = [];

    if (title) document.title = title;

    const upsertMeta = (attr, key, val) => {
      if (!val) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      const priorContent = el.getAttribute("content");
      el.setAttribute("content", val);
      tagRefs.push({ el, created, priorContent });
    };

    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("name", "twitter:card", ogImage ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);

    // Canonical link
    let canonEl = null;
    let canonCreated = false;
    let canonPrior = null;
    if (canonical) {
      canonEl = document.head.querySelector('link[rel="canonical"]');
      if (!canonEl) {
        canonEl = document.createElement("link");
        canonEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonEl);
        canonCreated = true;
      } else {
        canonPrior = canonEl.getAttribute("href");
      }
      canonEl.setAttribute("href", canonical);
    }

    // schema.org JSON-LD
    let schemaEl = null;
    if (schema) {
      schemaEl = document.createElement("script");
      schemaEl.type = "application/ld+json";
      schemaEl.setAttribute("data-seo-hook", "1");
      schemaEl.text = JSON.stringify(schema);
      document.head.appendChild(schemaEl);
    }

    return () => {
      document.title = prev.title;
      tagRefs.forEach(({ el, created, priorContent }) => {
        if (created) el.remove();
        else if (priorContent !== null) el.setAttribute("content", priorContent);
      });
      if (canonEl) {
        if (canonCreated) canonEl.remove();
        else if (canonPrior !== null) canonEl.setAttribute("href", canonPrior);
      }
      if (schemaEl) schemaEl.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, ogImage, ogType, JSON.stringify(schema)]);
}
